/**
 * Display helpers for recurring tournament series (admin#67).
 *
 * Presentation-only, same discipline as `tournaments.ts` and `coach-series.ts`:
 * these turn fields the tournament-series endpoints already return into
 * strings/colors staff read. No conflict, freeze, or eligibility rule lives
 * here — that is `prospin-api`'s (api#156). Where this file categorizes a
 * skip `reason` into "frozen" vs. "skipped", it is reading the API's own
 * verdict off the wire, not recomputing one — see `isFrozenBySeriesEdit`'s
 * doc.
 */
import { PS } from './theme';
import { weekdayLabel } from './coach-series';
import { occupancyLine } from './occupancy';
import type {
  SlotConflictDetail,
  SeriesOccurrenceEditSkipReason,
  TournamentSeriesStatus,
} from './types';

export function tournamentSeriesStatusLabel(status: TournamentSeriesStatus): string {
  return status === 'active' ? 'Active' : 'Ended';
}

export function tournamentSeriesStatusColor(status: TournamentSeriesStatus): string {
  return status === 'active' ? PS.success : PS.textSec;
}

/**
 * The series recurrence rule in words. Every tournament series is weekly by
 * schema (a single `day_of_week`), so the cadence is a constant, not
 * something inferred from the data — same reasoning as `coach-series.ts`'s
 * `recurrenceLabel`.
 */
export function tournamentSeriesRecurrenceLabel(dayOfWeek: number): string {
  return `Weekly · every ${weekdayLabel(dayOfWeek)}`;
}

/**
 * Why a series-wide content PATCH left one already-materialized occurrence
 * alone, in words. Vocabulary is `prospin-api`'s (`_eligible_occurrences`) —
 * this only translates it, and an unrecognised reason renders as-is rather
 * than mapped to a catch-all, so a reason the API adds later still reaches
 * the screen (same rule as `coach-series.ts`'s `occurrenceSkipLabel`).
 */
const SERIES_EDIT_SKIP_REASON_LABELS: Record<string, string> = {
  elapsed: 'Already started',
  cancelled: 'Cancelled',
  has_participants: 'Has participant history',
};

export function seriesOccurrenceEditSkipLabel(reason: SeriesOccurrenceEditSkipReason): string {
  return SERIES_EDIT_SKIP_REASON_LABELS[reason] ?? reason;
}

/**
 * Whether a skipped occurrence's reason is "frozen" (has participant
 * history) rather than merely out of scope for the bulk edit (elapsed,
 * cancelled). This is a rendering split over a value the API already
 * decided and reported — `reason` — never a recomputed eligibility check.
 * A "frozen" occurrence is the one case the UI must point staff at the
 * one-off `/tournaments/{id}` page for further edits, per the issue's brief.
 */
export function isFrozenBySeriesEdit(reason: SeriesOccurrenceEditSkipReason): boolean {
  return reason === 'has_participants';
}

/**
 * One-line summary of a materializer/bootstrap skip's `conflict_detail`
 * (api#156's `_conflict_detail`, the same `{error, code, conflicts[],
 * next_free_time}` envelope as api#81). Falls back to the envelope's own
 * sentence, or a generic one, when there's nothing to line up per-slot —
 * a shape this client hasn't seen yet should never crash the page.
 */
export function seriesConflictSummary(detail: SlotConflictDetail | null | undefined): string {
  if (!detail) return 'Conflict (no further detail)';
  if (Array.isArray(detail.conflicts) && detail.conflicts.length > 0) {
    return detail.conflicts.map((c) => occupancyLine(c)).join('; ');
  }
  return detail.error || 'Conflict (no further detail)';
}
